import { Box, Stack, Typography } from '@mui/material';
import { GridValidRowModel } from '@mui/x-data-grid-pro';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import { createFormatter, formatNumber, NumberFormatPreset, useCurrency } from '@procureai/ui-mui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { StatusUnion } from '~/common/types';
import { NULLISH_CELL_VALUE } from '~/components/DataGridCustom/DataGridCustom.types';
import { colorFromStatus } from '~/util/conversion';

import { InfoStyled } from './LineItemCell.styled';

export type LineItemCellValue =
  | {
      quotedTotalSum: number;
      quotedPricePerUnit: number;
      discountedPricePerUnit: Union.Nullable<number>;
      discountedTotalSum: Union.Nullable<number>;
      discountedTotalWithTaxes: Union.Nullable<number>;
      amount: number;
      unit: string;
      title: string;
      description?: string;
      currency: string;
      status?: Union.Nullable<StatusUnion>;
    }
  | null
  | undefined;

interface LineItemCellProps<R extends GridValidRowModel = any> {
  value: LineItemCellValue;
  row: R;
}

export const LineItemCell = <R extends GridValidRowModel = any>({ value }: LineItemCellProps<R>) => {
  const currencyCode = value?.currency || undefined;
  const { format } = useCurrency({
    code: currencyCode,
  });
  const { i18n } = useTranslation();
  const formatExact = useMemo<(amount: number) => string>(() => {
    if (currencyCode) {
      return createFormatter(i18n.language, currencyCode).formatNumber(NumberFormatPreset.CURRENCY_EXTENDED);
    }

    return formatNumber(NumberFormatPreset.DECIMAL_EXTENDED);
  }, [currencyCode, i18n.language]);

  if (value) {
    const statusColor = value.status ? colorFromStatus(value.status) : undefined;
    const totalSum = value.discountedTotalSum ?? value.quotedTotalSum;
    const pricePerUnit = value.discountedPricePerUnit ?? value.quotedPricePerUnit;
    const roundedPricePerUnit = format(pricePerUnit);
    const exactPricePerUnit = formatExact(pricePerUnit);
    const numericPricePerUnit = Number(pricePerUnit);
    const showPriceTooltip = Number.isFinite(numericPricePerUnit)
      ? Number(numericPricePerUnit.toFixed(2)) !== numericPricePerUnit
      : false;
    return (
      <div>
        <Typography component="div" variant="subtitle2">
          {value.title}
          {value.description ? (
            <Tooltip>
              <TooltipTrigger delay={0} render={<InfoStyled fontSize="small" />} />
              <TooltipContent className="whitespace-pre-line">{value.description}</TooltipContent>
            </Tooltip>
          ) : null}
        </Typography>
        <Typography component="div" variant="body2" color={statusColor ? `${statusColor}.main` : 'text.secondary'}>
          <Stack>
            <Box>
              <span>{format(totalSum)}</span>{' '}
              <Typography component="span" variant="caption" color="text.secondary" noWrap>
                ({value.amount} <CuriosityValue ns={CuriosityNamespaceType.UNIT} i18nKey={value.unit} /> x{' '}
                {showPriceTooltip ? (
                  <Tooltip>
                    <TooltipTrigger render={<span>{`~${roundedPricePerUnit}`}</span>} />
                    <TooltipContent>{exactPricePerUnit}</TooltipContent>
                  </Tooltip>
                ) : (
                  roundedPricePerUnit
                )}
                )
              </Typography>
            </Box>
          </Stack>
        </Typography>
      </div>
    );
  }

  return <>{NULLISH_CELL_VALUE}</>;
};
